<template>
  <BaseModal :title="$t('Create new project')" need-close-emit @close="closeModalHandler">
    <div class="form-window">
      <div class="two-form-items two-form-items_narrow">
        <AddProjectFormInput
          :id="'project_name'"
          v-model="form.name"
          :required="true"
          :placeholder="$t('Enter project name...')"
          :label-text="$t('Project name')"
          :error="$v.form.name.$error"
          @input="markFormAsChanged"
        />
        <Autocomplete
          v-model="form.currency"
          :label-text="$t('Project currency')"
          :items="currencyItems"
          required
        />
      </div>
      <div class="two-form-items two-form-items_narrow">
        <AddProjectFormInput
          id="project-address"
          v-model="form.address"
          required
          :placeholder="$t('Enter address...')"
          :label-text="$t('Project address')"
          :error="$v.form.address.$error"
          @input="markFormAsChanged"
        />
        <Select
          :label-text="$t('Project stage')"
          :items="computedProjectStates"
          :value="form.state"
          required
          @input="changeSelectedProjectState"
        />
      </div>

      <InputButtons :inputs="hiddenInputs" @open="handleInputOpen" />

      <div class="formcell">
        <label class="label" for="avatars">{{ $t('Add team members to the project') }}</label>
        <MemberAvatars
          id="avatars"
          :team="team"
          :members="selectedMembers"
          size="32"
          @select="form.user_ids = $event"
        />
      </div>

      <FormTextArea
        v-if="openedInputs.includes('description')"
        id="project-description"
        v-model="form.description"
        :label-text="$t('Project description')"
        :placeholder="$t('Enter project description...')"
        :max-length="512"
        @input="markFormAsChanged"
      />
      <div v-if="openedInputs.includes('color')" class="formcell">
        <label class="label">{{ $t('Project color') }}</label>
        <div class="project-styles-wrap">
          <ProjectColorDropdown
            ref="colorEmojiPicker"
            no-changes
            with-button
            @emoji="(emo) => (form.emoji = emo)"
            @color="changeColor"
          />
        </div>
      </div>
      <AddProjectFormInput
        v-if="openedInputs.includes('customer')"
        id="project-customer"
        v-model="form.customer"
        :placeholder="$t('Enter customer...')"
        :label-text="$t('Customer')"
        @input="markFormAsChanged"
      />

      <div v-if="openedInputs.includes('dates')" class="two-form-items">
        <DatePickerInput
          :id="'price-submission'"
          v-model="form.price_submission_date"
          :placeholder="$t('DD/MM/YY')"
          :min="minDeadlineDate"
          :max="maxDeadlineDate"
          :label-text="$t('Price submission deadline')"
          type="date"
          top
          @input="markFormAsChanged"
        />

        <DatePickerInput
          :id="'construction-date'"
          v-model="form.construction_beginning_date"
          :placeholder="$t('DD/MM/YY')"
          :min="minDeadlineDate"
          :max="maxDeadlineDate"
          :label-text="$t('Construction begins on')"
          type="date"
          top
          @input="markFormAsChanged"
        />
      </div>
      <FileAttachment
        :files="files"
        :default-label="false"
        @file-input="handleFileInput"
        @file-delete="handleFileDelete"
      >
        <template #label>
          You can <span class="link">use our Excel template</span> and upload it here. <br />
          Or prepare your existing <strong>.xlsx</strong> or <strong>.xls</strong> bill of quantity.
          <span class="link">Here's the short guide</span> on how to do that.
        </template>
      </FileAttachment>
    </div>
    <template #footer>
      <div class="spacex-12 d-contents">
        <Button type="secondary" @click="closeModalHandler">{{ $t('Close') }}</Button>
        <Button :loading="isLoading" @click="submit">{{ $t('Create project') }}</Button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex';
  import ProjectColorDropdown from '@/containers/top-menu/header/project-color/Dropdown';
  import AddProjectFormInput from '@/components/ui/FormInput';
  import FileAttachment from '@/components/projects/FileAttachment';
  import parseError from '@/utils/formatting/parse-error';
  import FormTextArea from '@/components/ui/FormTextArea';
  import { currentDate, datePlus } from '@/utils/date/current-date';
  import BaseModal from '@/components/ui/BaseModal';
  import DatePickerInput from '@/components/ui/DatePickerInput';
  import factory from '@/utils/api/factory';
  import Select from '@/components/ui/Select';
  import capitalize from '@/utils/formatting/capitalize';
  import Autocomplete from '@/components/ui/Autocomplete';
  import { DEFAULT_COLOR, DEFAULT_CURRENCY } from '@/utils/constants/common';
  import Button from '@/components/ui-v2/Button';
  import InputButtons from '@/components/ui/InputButtons';
  import { validationMixin } from 'vuelidate';
  import { required } from 'vuelidate/lib/validators';
  import teamQuery from '@/gql/projects/team.graphql';
  import MemberAvatars from '@/components/tenders/MemberAvatars';

  export default {
    name: 'AddProjectModal',
    components: {
      MemberAvatars,
      Select,
      DatePickerInput,
      BaseModal,
      ProjectColorDropdown,
      AddProjectFormInput,
      FileAttachment,
      FormTextArea,
      Autocomplete,
      Button,
      InputButtons,
    },
    mixins: [validationMixin],
    apollo: {
      team() {
        return {
          query: teamQuery,
          variables() {
            return {
              id: this.$store.state.auth?.user?.membership?.id,
            };
          },
          update(data) {
            if (data?.team?.length)
              return data.team[0].memberships
                .filter((membership) => membership.role === 'regular')
                .map((membership) => membership.user);
            return { memberships: [] };
          },
        };
      },
    },
    data() {
      return {
        form: {
          isFormChanged: false,
          name: null,
          address: null,
          description: null,
          price_submission_date: null,
          construction_beginning_date: null,
          customer: null,
          color: DEFAULT_COLOR,
          emoji: null,
          state: 'price_tendering',
          currency: DEFAULT_CURRENCY,
          user_ids: [],
        },
        files: [],
        isLoading: false,
        error: null,
        success: false,
        minDeadlineDate: currentDate(),
        maxDeadlineDate: datePlus(0, 0, 5),
        openedInputs: [],
        hiddenInputs: [
          {
            title: this.$t('Project description'),
            slug: 'description',
          },
          {
            title: this.$t('Project color'),
            slug: 'color',
          },
          {
            title: this.$t('Customer'),
            slug: 'customer',
          },
          {
            title: this.$t('Submission deadline'),
            slug: 'dates',
          },
        ],
      };
    },
    validations: {
      form: {
        name: {
          required,
        },
        address: {
          required,
        },
      },
    },
    created() {
      this.form.currency = this.companyCurrency;
    },
    computed: {
      ...mapState({
        activeCompany: (state) => state.auth.user?.membership,
        projectStates: (state) => state.global.project_states,
        computedProjectStates() {
          const items = [];
          for (let elem in this.projectStates) {
            let text = this.projectStates?.[elem];
            items.push({ text: capitalize(text), value: elem });
          }
          return items;
        },
      }),
      ...mapGetters({
        currencyItems: 'global/currencySelectItems',
        companyCurrency: 'auth/companyCurrency',
      }),
      selectedMembers() {
        return this.team?.filter((member) => this.form.user_ids.includes(member.id)) || [];
      },
    },
    methods: {
      ...mapActions({
        setModalAction: 'modal/setModalAction',
        closeModal: 'modal/closeModal',
        confirm: 'confirmModal/openModal',
        showAlert: 'alert/showAlert',
      }),
      updateMembers() {
        this.$apollo.queries.projectInfo.refetch();
      },
      handleInputOpen(slug) {
        this.openedInputs.push(slug);
      },
      changeSelectedProjectState(value) {
        this.form.state = value;
      },
      changeColor(col) {
        this.form.color = col;
        this.markFormAsChanged();
      },
      markFormAsChanged() {
        this.isFormChanged = true;
      },
      closeModalHandler() {
        if (this.isFormChanged) {
          this.confirm({
            title: this.$t('Cancel project?'),
            actionText: this.$t('Cancel Project'),
            description: this.$t('All the changes you made in this window will be deleted.'),
            revertButtons: true,
            cancelText: this.$t('Return'),
            style: 'danger',
            action: () => {
              this.closeModal();
              this.isFormChanged = false;
            },
          });
        } else {
          this.closeModal();
        }
      },
      async submit() {
        this.$v.$touch();
        if (this.$v.$invalid) return;
        this.isLoading = true;

        try {
          const res = await factory('projects').create(this.form);
          if (this.files[0]) {
            const data = new FormData();
            data.append('file', this.files[0]);
            await factory('projects').patchInPath(res.data.id, data);
          }
          this.success = true;
          this.error = null;
          this.setModalAction({
            type: 'projectAdded',
            projectId: res.data.id,
            importNeeded: !!this.files[0],
          });
          this.$emit('close');
        } catch (e) {
          this.error = parseError(e) || this.$t('Sorry, something went wrong.');
          this.showAlert({
            type: 'error',
            text: this.error,
          });
          this.success = false;
        }
        this.isLoading = false;
      },
      handleFileInput(files) {
        this.markFormAsChanged();
        this.files = [files[0]];
      },
      handleFileDelete() {
        this.files = [];
      },
    },
  };
</script>
